import { config } from '@/lib/config';
import { KNOWN_EMITTERS } from './schemas/surveillance';
import { outputTopics, v1Topics } from './topics';

/**
 * Legacy (v0) flat topics. Still live on the airmarket / sait tenants.
 *
 * - `networkid/#`      — per-drone telemetry (primary 1 Hz stream).
 * - `traffic_control`  — drone status backfill.
 * - `TISB Stream/+`    — TIS-B transponder status per drone (note: the topic
 *                        name legitimately contains a space).
 * - `flightplan/+`     — mission plan per drone → OI polygon + metadata.
 *
 * All four return zero messages on sandbox since the 2026-07-30 cut-over.
 */
const LEGACY_DRONE_TOPICS: readonly string[] = [
  'networkid/#',
  'traffic_control',
  'TISB Stream/+',
  'flightplan/+',
  // Canonical v1 Events & Alerts (FLYsafe MQTT Topic doc). `+` is the serial
  // slot → all drones. `/alerts` and `/alerts/+` are distinct MQTT levels, so
  // both are needed to catch severity-suffixed alerts.
  v1Topics.droneEventsWild,
  v1Topics.aircraftAlertsWild,
  v1Topics.aircraftAlertsSevWild,
  v1Topics.broadcastAlerts,
];

/**
 * Category-first (v1) per-aircraft topics — ticket 86e2jbe3d.
 *
 * Subscribed by exact message type, NOT by wildcard. `v1/output/#` and
 * `v1/output/aircraft/+/#` both pull in the per-field alias branch
 * (`.../{sn}/drones/{fieldName}`), which republishes all 56 NID fields as
 * individual messages: 97 msg/s per aircraft against 3.5 msg/s for the
 * consolidated pair, carrying no data the pair doesn't already have.
 *
 * `oi/declaration` is listed even though it wasn't publishing at first —
 * it's in the namespace and lit up once the backend enabled it. NOT
 * listed: `v1/output/aircraft/{sn}/events` — the NID/NID-DD jobs briefly
 * published there before being corrected to the doc-specified tenant-wide
 * `v1/output/events` (see `outputTopics.tenantEvents` below, and the note
 * on `outputTopics` in topics.ts). Confirmed dead: 0 messages across every
 * check this session.
 */
const OUTPUT_DRONE_TOPICS: readonly string[] = [
  outputTopics.networkidWild,
  outputTopics.telemetryWild,
  outputTopics.oiDeclarationWild,
  outputTopics.waypointPredWild,
  outputTopics.waypointApproachedWild,
  outputTopics.tenantEvents,
  outputTopics.tenantAlertsWild,
];

/**
 * Streams that have NOT moved namespace. Verified on sandbox 2026-07-30:
 * `v1/shared/#` is completely silent while these flat topics are still
 * publishing, so they stay subscribed regardless of `mqtt.schema`.
 *
 * `flysafe/v1/#` is the MAVLink envelope family — it backfills gps_sats,
 * signal, battery_v and the real autopilot flight mode, none of which the
 * new NID topics carry. It is also 82 kB/s, ~83% of our total bandwidth,
 * so it is the first candidate to drop once `c2links` ships.
 *
 * Surveillance services are DISCOVERED from `surveillance_sensors/+` (one
 * snapshot every 30-90 s per service, ~11 KB/s even counting the non-service
 * `live_community` / `stream` snapshots the handler drops) and each newly seen
 * service's `surveillance_traffic/<id>` is subscribed at that moment — see
 * `MqttSourceLegacy.handleSurveillanceSensors`. A traffic wildcard is NOT used:
 * it would also pull `live_ads_count` / `stream` / `live_community`, measured at
 * ~540 KB/s per tab against ~93 KB/s for the services themselves. The curated
 * services' traffic is subscribed up front so their targets flow from the first
 * second (ticket 86e1fvf06).
 */
const SHARED_TOPICS: readonly string[] = [
  'flysafe/v1/#',
  'surveillance_sensors/+',
  ...KNOWN_EMITTERS.map((id) => `surveillance_traffic/${id}`),
  'weather/sensors',
  // RF drone detection (ENG-253). INBOUND_DATA is intentionally not subscribed.
  'v1/ingest/live_rf/REAL_TIME_DATA',
  'v1/ingest/live_rf/HEARTBEAT',
];

/**
 * Every topic filter `MqttSourceLegacy` subscribes to on connect, in order.
 *
 * Single source of truth: the broker subscribe loop *and* the Data Stream
 * topic tree (which seeds these as "subscribed but silent" placeholders)
 * both read this list, so the two can never drift.
 *
 * The optional `#` wildcard (UI "Subscribe all") is toggled separately via
 * `setWildcard()` and is intentionally NOT part of this static list.
 */
export function buildSubscriptions(schema = config.mqtt.schema): readonly string[] {
  const drone =
    schema === 'output'
      ? OUTPUT_DRONE_TOPICS
      : schema === 'both'
        ? [...LEGACY_DRONE_TOPICS, ...OUTPUT_DRONE_TOPICS]
        : LEGACY_DRONE_TOPICS;
  return [...drone, ...SHARED_TOPICS];
}

export const LEGACY_SUBSCRIPTIONS: readonly string[] = buildSubscriptions();

/**
 * Topics used by the Data Stream "subscribe all" toggle.
 *
 * A bare `#` is REFUSED by the sandbox broker — its ACL grants
 * `v1/shared/#` and `v1/output/#` only (confirmed: SUBACK 128 on `#`).
 * And `v1/output/#` would itself drag in the per-field alias branch, so
 * the output side uses `v1/output/aircraft/+/+`, which is five levels and
 * therefore cannot match the six-level `.../drones/{fieldName}`.
 */
export const WILDCARD_SUBSCRIPTIONS: readonly string[] =
  config.mqtt.schema === 'legacy'
    ? ['#']
    : [outputTopics.allPerAircraft, `${outputTopics.tenantEvents}/#`, 'v1/shared/#'];

// The MQTT source is a singleton on `globalThis` (source.ts) that survives Vite
// HMR, and it subscribes to this list exactly once, when it connects. A tab that
// was open while this list (or the source's routing) changed therefore keeps the
// OLD subscriptions and the old class code — new topics simply never arrive, which
// the Debug panel shows as empty rows (seen 2026-10-07 for the waypoint topics).
// Only a fresh connection fixes that, so reload instead of hot-swapping.
if (import.meta.hot) {
  import.meta.hot.accept(() => window.location.reload());
}
